/**
 * Walk the frozen TipTap doc in the share snapshot and assign a 1-based
 * global index to each cited source — the order they first appear in
 * the document. This is the JS twin of the backend's
 * ``core/export/indices.assign_global_indices`` so the share view's
 * reference numbering matches the exported deck a paid user would
 * download for the same draft.
 *
 * Per-claim ``review_state.hidden_source_ids`` are respected: a citation
 * whose every source has been hidden contributes nothing to the
 * numbering; a citation with some hidden + some visible only counts
 * the visible ones. Mirrors the in-app reviewedDoc filter.
 */
import type { JSONContent } from '@/types/api'
import type { ShareSnapshot } from '@/types/share'

export interface CitationOccurrence {
  /** Numbers rendered inside the citation ``<sup>`` for this node — the
   *  visible sources for this citation, in global-index order. May be
   *  empty when every source on this citation has been hidden in
   *  review_state. */
  indices: number[]
  /** ``claimId`` attr from the citation node, copied through so the
   *  document renderer can wire clicks back to the matching claim. */
  claimId: string
}

export interface SnapshotIndices {
  /** Map from citation-node identity (``${nodeIdx}`` walking order) to
   *  the indices it renders. ShareDocument walks the doc in the same
   *  order and pulls by counter. */
  perCitation: CitationOccurrence[]
  /** Global ordering of cited source IDs — index ``i`` in this array
   *  is reference number ``i+1`` in the bibliography. */
  orderedSourceIds: string[]
}

interface CitationAttrs {
  claimId?: string | null
  sourceUuids?: string | null
}

function parseSourceUuids(raw: string | null | undefined): string[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

export function buildSnapshotIndices(snapshot: ShareSnapshot): SnapshotIndices {
  // claim_uuid → set of hidden source ids (frozen at publish time on
  // each claim's review_state).
  const hiddenByClaim = new Map<string, Set<string>>()
  for (const c of snapshot.claims) {
    const ids = c.review_state?.hidden_source_ids ?? []
    if (ids.length > 0) hiddenByClaim.set(c.id, new Set(ids))
  }

  const globalIndex = new Map<string, number>()
  const orderedSourceIds: string[] = []
  const perCitation: CitationOccurrence[] = []

  function visit(node: JSONContent | undefined): void {
    if (!node || typeof node !== 'object') return
    const obj = node as Record<string, unknown>
    if (obj.type === 'citation') {
      const attrs = (obj.attrs ?? {}) as CitationAttrs
      const claimId = attrs.claimId ?? ''
      const sourceUuids = parseSourceUuids(attrs.sourceUuids)
      const hidden = hiddenByClaim.get(claimId) ?? new Set<string>()
      const indices: number[] = []
      for (const uuid of sourceUuids) {
        if (hidden.has(uuid)) continue
        let idx = globalIndex.get(uuid)
        if (idx === undefined) {
          idx = orderedSourceIds.length + 1
          globalIndex.set(uuid, idx)
          orderedSourceIds.push(uuid)
        }
        indices.push(idx)
      }
      perCitation.push({ indices, claimId })
      return
    }
    const children = obj.content
    if (Array.isArray(children)) {
      for (const child of children) visit(child as JSONContent)
    }
  }

  visit(snapshot.draft.content)
  return { perCitation, orderedSourceIds }
}
